import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import { App } from 'antd';
import { Icon } from '../icons/icon.js';
/**
 * Imperative confirmation, shared by both panels.
 *
 * ```tsx
 * const confirm = useConfirm();
 *
 * async function handleDelete() {
 *   if (!(await confirm({
 *     title: 'حذف پروندهٔ مراجع؟',
 *     content: 'پرونده به سطل بازیافت می‌رود و تا ۹۰ روز قابل بازیابی است.',
 *     danger: true,
 *   }))) return;
 *   await remove();
 * }
 * ```
 *
 * ── WHY NOT `Modal.confirm` ─────────────────────────────────────────────────
 *
 * The static import renders its own React root, outside `ConfigProvider`. That
 * means no theme, no `fa-IR` locale and — the reason it matters most here — no
 * `direction="rtl"`: the footer comes out LTR, so on a destructive dialog the
 * red confirm button lands where the user's eye and muscle memory expect
 * Cancel. `App.useApp().modal` renders inside the tree and inherits all three.
 * The kit does not export `Modal.confirm` at all; this is the only door.
 *
 * ── WHY IT MOVED OUT OF THE CLINIC PANEL ────────────────────────────────────
 *
 * The console was calling `modal.confirm` inline in five places with five
 * different button labels and no consistent danger treatment. Both panels ask
 * the same kind of question and it should look the same in both.
 *
 * Defaults are «تأیید»/«انصراف». A destructive action should override `okText`
 * with the verb («حذف شود») so the button says what it does rather than
 * agreeing with a question the user has already stopped reading.
 */
export function useConfirm() {
    const { modal } = App.useApp();
    return useCallback((options) => new Promise((resolve) => {
        const danger = options.danger ?? false;
        modal.confirm({
            title: options.title,
            content: options.content,
            icon: _jsx(Icon, { name: danger ? 'danger' : 'warning', size: 20, active: true }),
            okText: options.okText ?? 'تأیید',
            cancelText: options.cancelText ?? 'انصراف',
            okButtonProps: { danger },
            // A destructive dialog opens with CANCEL focused. Enter is the most
            // common way to dismiss a dialog you did not read.
            autoFocusButton: danger || options.focusCancel ? 'cancel' : 'ok',
            centered: true,
            maskClosable: true,
            onOk: () => {
                resolve(true);
            },
            onCancel: () => {
                resolve(false);
            },
        });
    }), [modal]);
}
//# sourceMappingURL=use-confirm.js.map